import { apiClient } from '@/lib/apiClient';

export interface EmailSignature {
  id: string;
  name: string;
  body_html?: string;
  body_text?: string;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailSignatureInput {
  name: string;
  body_html?: string;
  body_text?: string;
  is_default?: boolean;
}

interface EmailSignatureListResponse {
  success: boolean;
  signatures?: EmailSignature[];
  message?: string;
}

interface EmailSignatureResponse {
  success: boolean;
  id?: string;
  signature?: EmailSignature;
  message?: string;
}

/**
 * Fetch all signatures for the current user.
 */
export const fetchEmailSignatures = async (): Promise<EmailSignature[]> => {
  const response = await apiClient.get<EmailSignatureListResponse>('/email/signatures');
  return response.signatures || [];
};

/**
 * Fetch just the user's default signature (for pre-selecting on a new compose).
 */
export const fetchDefaultEmailSignature = async (): Promise<EmailSignature | null> => {
  const response = await apiClient.get<EmailSignatureListResponse>('/email/signatures?default=true');
  const list = response.signatures || [];
  return list.find((s) => s.is_default) || list[0] || null;
};

/**
 * Create a new signature. Setting is_default un-defaults the rest server-side.
 */
export const createEmailSignature = async (
  data: EmailSignatureInput
): Promise<string | null> => {
  const response = await apiClient.post<EmailSignatureResponse>('/email/signatures', data);
  return response.id || response.signature?.id || null;
};

/**
 * Update an existing signature (partial).
 */
export const updateEmailSignature = async (
  id: string,
  data: Partial<EmailSignatureInput>
): Promise<void> => {
  await apiClient.put<EmailSignatureResponse>(`/email/signatures/${id}`, data);
};

/**
 * Delete a signature.
 */
export const deleteEmailSignature = async (id: string): Promise<void> => {
  await apiClient.delete<EmailSignatureResponse>(`/email/signatures/${id}`);
};
